import type { QueryArrayParamStyle } from "./types/client";

export function ensureAllKeys<
  Full extends object,
  Reference extends { [K in keyof Full]: unknown },
>(obj: Partial<Full>, reference: Reference): obj is Full {
  for (const _key of Object.keys(reference)) {
    const key = _key as keyof Reference;
    if (!Object.hasOwn(obj, key)) {
      return false;
    }
  }
  return true;
}

const SUPPORTED_RETURN_TYPES = ["application/json", "application/vnd.apache.arrow.stream"] as const;
type SupportedReturnType = (typeof SUPPORTED_RETURN_TYPES)[number];
export function isSupportedReturnType(type: string): type is SupportedReturnType {
  return (SUPPORTED_RETURN_TYPES as readonly string[]).includes(type);
}

export function allAsString(obj: Record<string, unknown>): Record<string, string> {
  const record: Record<string, string> = {};

  for (const [name, value] of Object.entries(obj)) {
    if (value === null || value === undefined) {
      continue;
    }
    record[name] = `${value}`;
  }

  return record;
}

export function buildQueryParams(
  obj: Record<string, unknown>,
  arrayParamStyle: QueryArrayParamStyle,
): URLSearchParams {
  const params = new URLSearchParams();

  for (const [name, value] of Object.entries(obj)) {
    if (value === null || value === undefined) {
      continue;
    }

    if (Array.isArray(value) && arrayParamStyle !== "comma") {
      const queryName = arrayParamStyle === "brackets" ? `${name}[]` : name;

      for (const item of value) {
        params.append(queryName, `${item}`);
      }

      continue;
    }

    params.append(name, `${value}`);
  }

  return params;
}

export function replaceParams(url: string, params: Record<string, string>) {
  for (const [name, value] of Object.entries(params)) {
    url = url.replace(`{${name}}`, value);
  }
  return url;
}

export function buildMultipartFormData(body: unknown): FormData {
  if (body instanceof FormData) {
    return body;
  }

  if (body === null || typeof body !== "object" || Array.isArray(body) || body instanceof Blob) {
    throw new TypeError("multipart/form-data bodies must be plain objects or FormData");
  }

  const formData = new FormData();
  for (const [name, value] of Object.entries(body)) {
    appendMultipartValue(formData, name, value);
  }
  return formData;
}

function appendMultipartValue(formData: FormData, name: string, value: unknown): void {
  if (value === null || value === undefined) {
    return;
  }

  if (Array.isArray(value)) {
    for (const item of value) {
      appendMultipartValue(formData, name, item);
    }
    return;
  }

  if (value instanceof Blob) {
    formData.append(name, value);
    return;
  }

  if (typeof value === "object") {
    if (containsBlob(value)) {
      for (const [childName, childValue] of Object.entries(value)) {
        appendMultipartValue(formData, `${name}[${childName}]`, childValue);
      }
      return;
    }

    formData.append(name, JSON.stringify(value));
    return;
  }

  formData.append(name, `${value}`);
}

function containsBlob(value: unknown): boolean {
  if (value instanceof Blob) {
    return true;
  }

  if (Array.isArray(value)) {
    return value.some(containsBlob);
  }

  if (value !== null && typeof value === "object") {
    return Object.values(value).some(containsBlob);
  }

  return false;
}
